import { isStepActiveForMode } from '../routine/DayModeRules';
import { DayMode } from '../routine/enums';
import type { DailyStep } from '../routine/models';
import type { DailyStepId } from '../shared/ids';
import { AdaptationRuleId } from './enums';

/**
 * One rule id per day mode, or undefined when the mode requires no reordering (a valid,
 * explained "no adaptation needed" outcome, not a gap in coverage — normal mode already offers
 * the full flexible order the user chose, so re-prioritizing it would not serve the user).
 */
export const RULE_BY_MODE: Readonly<Record<DayMode, AdaptationRuleId | undefined>> = {
  [DayMode.normal]: undefined,
  [DayMode.busy]: AdaptationRuleId.requiredFirst,
  [DayMode.travel]: AdaptationRuleId.activeForModeFirst,
  [DayMode.recovery]: AdaptationRuleId.essentialFirst,
  [DayMode.difficult]: AdaptationRuleId.essentialThenRequired,
  [DayMode.elevation]: undefined,
  [DayMode.momentDecisif]: AdaptationRuleId.essentialThenRequired,
  [DayMode.imprevu]: AdaptationRuleId.requiredFirst,
  [DayMode.transition]: undefined,
};

function priorityKey(step: DailyStep, mode: DayMode, ruleId: AdaptationRuleId): readonly number[] {
  switch (ruleId) {
    case AdaptationRuleId.requiredFirst:
      return [step.required ? 0 : 1];
    case AdaptationRuleId.activeForModeFirst:
      return [isStepActiveForMode(step, mode) ? 0 : 1];
    case AdaptationRuleId.essentialFirst:
      return [step.essential === true ? 0 : 1];
    case AdaptationRuleId.essentialThenRequired:
      return [step.essential === true ? 0 : 1, step.required ? 0 : 1];
  }
}

/**
 * Stable sort by the rule's priority key, preserving the user's chosen relative order within
 * each priority group. Returns the full ordered id list — the caller decides whether this
 * differs from the current order before treating it as a real candidate.
 */
export function computePriorityOrder(
  steps: readonly DailyStep[],
  mode: DayMode,
  ruleId: AdaptationRuleId,
): readonly DailyStepId[] {
  return steps
    .map((step, index) => ({ step, index }))
    .sort((a, b) => {
      const keyA = priorityKey(a.step, mode, ruleId);
      const keyB = priorityKey(b.step, mode, ruleId);
      for (let i = 0; i < keyA.length; i += 1) {
        const diff = (keyA[i] ?? 0) - (keyB[i] ?? 0);
        if (diff !== 0) return diff;
      }
      return a.index - b.index;
    })
    .map(({ step }) => step.id);
}
